import { useEffect, useState } from "react";
import { DayRange } from "features/customer-products/sales-by-period";
import { fetchProductSales } from "services/product-sales.service";
import { ProductSalesResponse } from "types/ProductSales";

export type SalesState =
  | { status: "loading" }
  | { status: "failed" }
  | { status: "loaded"; data: ProductSalesResponse };

type Answer = Exclude<SalesState, { status: "loading" }>;

/**
 * Sales of one product in a period. Every product and period is a question of
 * its own; an answer to an earlier question is never shown, the state is
 * "loading" until the current one is answered.
 */
export function useProductSales(companyId: string, productId: string, range: DayRange | null) {
  const [attempt, setAttempt] = useState(0);
  const [latest, setLatest] = useState<{ question: string; answer: Answer } | null>(null);
  const question = [companyId, productId, range && range.from, range && range.to, attempt].join("|");

  useEffect(() => {
    // False once the question changes or the tab closes, so a late answer is dropped.
    let isCurrent = true;
    fetchProductSales(companyId, productId, range)
      .then((data) => {
        if (isCurrent) setLatest({ question, answer: { status: "loaded", data } });
      })
      .catch(() => {
        if (isCurrent) setLatest({ question, answer: { status: "failed" } });
      });
    return () => {
      isCurrent = false;
    };
  }, [companyId, productId, range, question]);

  const state: SalesState = latest && latest.question === question ? latest.answer : { status: "loading" };
  return { state, retry: () => setAttempt((count) => count + 1) };
}
